import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, Compass } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { ProjectIcon } from "@/components/ProjectIcon";
import type { Domain, ProjectWithDomains, SiteSettings } from "@/lib/types";

const VALUE_PROPS = [
  {
    icon: Sparkles,
    title: "Built with care",
    body: "Every project we ship is crafted to be both genuinely useful and a pleasure to use.",
  },
  {
    icon: ShieldCheck,
    title: "Privacy first",
    body: "Your data is yours. We build software that respects it — no tracking, no surprises.",
  },
  {
    icon: Compass,
    title: "Made to be trusted",
    body: "Clear, deterministic systems you can understand — not black boxes you have to take on faith.",
  },
];

/** Signature flight-path: a migrating V of geese tracing a dotted arc — echoes
    the goose mark and the logo's network/trajectory graphics. Purely decorative. */
function FlightPath() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full text-teal/35"
      viewBox="0 0 1200 600"
      fill="none"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <path
        d="M-40 470 C 240 470, 380 250, 620 210 S 1020 130, 1260 70"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="2 10"
        strokeLinecap="round"
      />
      {[
        [150, 430],
        [360, 300],
        [560, 222],
        [780, 178],
        [1000, 130],
      ].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3.5" fill="currentColor" />
      ))}
      {[
        [620, 205, 1],
        [700, 172, 0.85],
        [760, 150, 0.7],
        [672, 232, 0.85],
        [732, 210, 0.7],
      ].map(([x, y, o], i) => (
        <path
          key={i}
          d={`M${x} ${y} l 16 8 l 16 -8`}
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={o as number}
        />
      ))}
    </svg>
  );
}

export function LandingPage() {
  const [projects, setProjects] = useState<ProjectWithDomains[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [settings, setSettings] = useState<SiteSettings>({});

  useEffect(() => {
    fetch("/api/projects").then((r) => r.json()).then(setProjects);
    fetch("/api/domains").then((r) => r.json()).then(setDomains);
    fetch("/api/settings").then((r) => r.json()).then(setSettings);
  }, []);

  return (
    <div>
      <Seo
        title="Loose Goose Systems"
        description="Loose Goose Systems LLC builds delightful apps, custom devices, and medical-adjacent projects — software, hardware, and the pipelines that tie them together."
        path="/"
      />
      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-background">
        <FlightPath />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,var(--ember)_0%,transparent_60%)] opacity-[0.09]" />
        <div className="container relative mx-auto px-6 py-24 md:py-32">
          <div className="mx-auto max-w-3xl text-center">
            <img
              src="/assets/lgs-logo-color.png"
              alt="Loose Goose Systems logo"
              className="mx-auto mb-8 h-36 w-36 drop-shadow-sm md:h-44 md:w-44"
            />
            <p className="mb-5 font-mono text-xs font-medium uppercase tracking-[0.28em] text-teal">
              Software · Devices · Medical
            </p>
            <h1 className="mb-6 font-display text-5xl font-extrabold tracking-tight text-foreground md:text-6xl">
              {settings.company_name || "Loose Goose Systems"}
            </h1>
            <p className="mx-auto mb-9 max-w-xl text-lg text-muted-foreground md:text-xl">
              {settings.tagline || "Software, devices, and medical projects — built with care."}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-ember px-8 text-base font-semibold text-ember-foreground hover:bg-ember/90"
              >
                <Link to="/projects">Explore projects</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8 text-base">
                <Link to="/domains">What we do</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Domains teaser */}
      <section className="container mx-auto px-6 py-20">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <h2 className="mb-3 font-display text-3xl font-bold tracking-tight">Three domains, one flock</h2>
          <p className="text-lg text-muted-foreground">
            We design software, build custom devices, and apply both to medical projects.
          </p>
        </div>
        <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
          {domains.map((d) => (
            <Link key={d.slug} to={`/domains/${d.slug}`} className="group">
              <Card className="h-full transition-all duration-300 group-hover:-translate-y-1 group-hover:border-teal/60 group-hover:shadow-lg">
                <CardHeader>
                  <div className="mb-3 text-5xl">{d.icon || "🦆"}</div>
                  <CardTitle className="font-display text-xl transition-colors group-hover:text-teal">
                    {d.name}
                  </CardTitle>
                  <CardDescription className="text-base">{d.tagline}</CardDescription>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured projects */}
      <section className="border-t bg-muted/30">
        <div className="container mx-auto px-6 py-20">
          <div className="mb-12 text-center">
            <h2 className="mb-3 font-display text-3xl font-bold tracking-tight">Featured projects</h2>
            <p className="text-lg text-muted-foreground">Crafted with care, designed with personality</p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {projects.slice(0, 6).map((project) => (
              <Link key={project.slug} to={`/projects/${project.slug}`} className="group">
                <Card className="h-full transition-all duration-300 group-hover:-translate-y-1 group-hover:border-teal/60 group-hover:shadow-lg">
                  <CardHeader>
                    <ProjectIcon icon={project.icon_url} size="text-4xl" className="mb-2" alt={project.name} />
                    <CardTitle className="font-display text-xl transition-colors group-hover:text-teal">
                      {project.name}
                    </CardTitle>
                    <CardDescription className="text-base">{project.tagline}</CardDescription>
                  </CardHeader>
                  {project.domains.length > 0 && (
                    <CardContent className="flex flex-wrap gap-1.5">
                      {project.domains.map((d) => (
                        <Badge key={d.id} domainSlug={d.slug}>
                          {d.icon && <span className="mr-1">{d.icon}</span>}
                          {d.name}
                        </Badge>
                      ))}
                    </CardContent>
                  )}
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="border-t">
        <div className="container mx-auto px-6 py-20">
          <div className="mx-auto grid max-w-5xl gap-10 text-center md:grid-cols-3">
            {VALUE_PROPS.map(({ icon: Icon, title, body }) => (
              <div key={title}>
                <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-xl bg-teal/10 text-teal">
                  <Icon className="size-6" />
                </div>
                <h3 className="mb-2 font-display text-lg font-semibold">{title}</h3>
                <p className="text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
